/**
 * FormBuilderCanvas Component
 * 7-tab form builder interface with full implementation
 *
 * Features:
 * - Design Tab: WYSIWYG drag-drop canvas with visual builder
 * - Controls Tab: Control palette with categorized controls
 * - Properties Tab: Property inspector for selected control
 * - Settings Tab: Form-level settings and metadata
 * - Preview Tab: Live form preview in different modes
 * - JSON Tab: Direct JSON schema editing
 * - Validation Tab: Validation rules configuration
 * - Undo/Redo: Full undo/redo support for all changes
 * - Keyboard Shortcuts: Delete, Ctrl+Z, Ctrl+Y, Ctrl+D
 */

import React, { useState, useCallback, useMemo, useRef } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { useUIStore } from '@atlas-forms/state-react';
import type { FormSchema, FormControl, FieldValidation } from '@atlas-forms/types-js';
import type { DesignerFormControl, DesignerControlPatch } from '../types/DesignerFormControl';
import { CheckIcon, BREAKPOINTS } from '@atlas-forms/ui-components-react';
import { FormField, FormThemeProvider } from '@atlas-forms/player-components-react';
import { DesignTab } from './tabs/DesignTab';
import { ControlsTab } from './tabs/ControlsTab';
import { PropertiesTab } from './tabs/PropertiesTab';
import { SettingsTab } from './tabs/SettingsTab';
import { PreviewTab } from './tabs/PreviewTab';
import { JsonTab } from './tabs/JsonTab';
import { ValidationTab } from './tabs/ValidationTab';
import { PluginsTab } from './tabs/PluginsTab';
import { UndoRedoManager } from '../utils/UndoRedoManager';
import { DuplicateControlDialog, PlacementOption } from './DuplicateControlDialog';
import { LoadingSpinner } from './LoadingSpinner';
import { Toast, ToastType } from './Toast';
import { FullscreenCanvasModal } from './FullscreenCanvasModal';
import '../styles/FormBuilderCanvas.css';

type TabType = 'design' | 'wysiwyg' | 'validation' | 'settings' | 'plugins' | 'preview' | 'json';

export interface FormBuilderCanvasProps {
  /** Initial form schema. Optional — defaults to an empty schema when not provided. */
  initialSchema?: FormSchema;
  onSchemaChange?: (schema: FormSchema) => void;
  onSave?: () => Promise<void>;
  /**
   * When provided, the internal tab bar is hidden and only this view is shown.
   * Used by FormDefinitionForm to drive the active view from outer tabs.
   */
  forcedTab?: TabType | 'wysiwyg';
  /**
   * Initial active tab when component mounts. Defaults to 'design'.
   * Ignored if forcedTab is set.
   */
  initialActiveTab?: TabType;
  /**
   * Called when user switches to a different tab
   */
  onTabChange?: (tab: TabType) => void;

  // Visibility control props from renderOptions
  hideDesignerTab?: boolean;
  hideJsonTab?: boolean;
  hideSettingsTab?: boolean;
  hideNavigationTabs?: boolean;
  hidePropertiesPanel?: boolean;
  hideFieldToolbox?: boolean;
  hideSaveButton?: boolean;
  defaultTab?: 'designer' | 'json' | 'settings';
}

const DEFAULT_SCHEMA: FormSchema = {
  version: '1.0',
  metadata: { formID: `form-${Date.now()}`, title: 'New Form' },
  controls: [],
};

/**
 * Error boundary to prevent blank screen when tab content throws
 */
class TabErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('FormBuilderCanvas tab render error:', error, info);
  }
  render() {
    if (this.state.error) {
      return (
        this.props.fallback ?? (
          <div style={{ padding: 24, textAlign: 'center', color: '#ef4444' }}>
            <p style={{ fontWeight: 600 }}>Something went wrong rendering this tab.</p>
            <p style={{ fontSize: 13, color: '#94a3b8' }}>{this.state.error.message}</p>
            <button
              type="button"
              onClick={() => this.setState({ error: null })}
              style={{
                marginTop: 12, padding: '6px 16px', borderRadius: 6,
                border: '1px solid #d1d5db', background: 'transparent',
                cursor: 'pointer', fontSize: 13,
              }}
            >
              Retry
            </button>
          </div>
        )
      );
    }
    return this.props.children;
  }
}

/**
 * FormBuilderCanvas - Main form builder interface with 7 functional tabs
 */
export const FormBuilderCanvas: React.FC<FormBuilderCanvasProps> = ({
  initialSchema,
  onSchemaChange,
  onSave,
  forcedTab,
  initialActiveTab = 'design',
  onTabChange,
  hideDesignerTab,
  hideJsonTab,
  hideSettingsTab,
  hideNavigationTabs,
  hidePropertiesPanel,
  hideFieldToolbox,
  hideSaveButton,
  defaultTab,
}) => {
  // Per-instance undo/redo manager (not shared across instances)
  const [undoRedoManager] = useState(() => new UndoRedoManager());

  // State management - simplified without schemaRef anti-pattern
  const [schema, setSchema] = useState<FormSchema>(() => initialSchema ?? DEFAULT_SCHEMA);
  // Use defaultTab if provided (mapped from 'designer' → 'design'), otherwise use initialActiveTab
  const resolveInitialTab = (): TabType => {
    if (defaultTab === 'designer') return 'design';
    if (defaultTab === 'json') return 'json';
    if (defaultTab === 'settings') return 'settings';
    return initialActiveTab;
  };
  const [activeTab, setActiveTab] = useState<TabType>(resolveInitialTab());
  const [selectedControlId, setSelectedControlId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [zoomLevel, setZoomLevel] = useState(100);
  const [showGrid, setShowGrid] = useState(true);
  const [undoCount, setUndoCount] = useState(0);
  const [redoCount, setRedoCount] = useState(0);
  const [undoActionPreview, setUndoActionPreview] = useState<string | null>(null);
  const [redoActionPreview, setRedoActionPreview] = useState<string | null>(null);
  const [selectedTabId, setSelectedTabId] = useState<string | null>(null);
  const [wysiwygDraggedId, setWysiwygDraggedId] = useState<string | null>(null);
  const [wysiwygDragOverId, setWysiwygDragOverId] = useState<string | null>(null);
  const [shortcutFeedback, setShortcutFeedback] = useState<string | null>(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [controlToDuplicate, setControlToDuplicate] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastType, setToastType] = useState<ToastType>('success');
  const [toastVisible, setToastVisible] = useState(false);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  const [apiActions, setApiActions] = useState<any[]>([]);

  const { theme } = useUIStore();
  const isDark = theme === 'dark';

  // Accessibility: Detect reduced motion preference (Priority 1.3)
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Below ~1024px the 3-pane (Palette/Canvas/Properties) horizontal split
  // becomes illegible slivers -- stack the panes vertically instead so each
  // one gets the full available width. 1024px+ keeps the existing
  // side-by-side layout unchanged.
  const [isNarrowLayout, setIsNarrowLayout] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(`(max-width: ${BREAKPOINTS.desktop - 1}px)`).matches;
  });

  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(`(max-width: ${BREAKPOINTS.desktop - 1}px)`);
    const handleChange = (e: MediaQueryListEvent) => setIsNarrowLayout(e.matches);
    setIsNarrowLayout(mq.matches);
    mq.addEventListener('change', handleChange);
    return () => mq.removeEventListener('change', handleChange);
  }, []);

  // Pane size presets: percentages of PanelGroup width (horizontal) or
  // height (vertical, when stacked on narrow viewports).
  const paletteSize = isNarrowLayout
    ? { defaultSize: 32, minSize: 20, maxSize: 55 }
    : { defaultSize: 15, minSize: 10, maxSize: 25 };
  const canvasSize = isNarrowLayout
    ? { defaultSize: 36, minSize: 20 }
    : { defaultSize: 60, minSize: 40 };
  const propertiesSize = isNarrowLayout
    ? { defaultSize: 32, minSize: 20, maxSize: 55 }
    : { defaultSize: 25, minSize: 15, maxSize: 35 };
  // Vertical stacking needs an explicit height for percentage sizing to
  // resolve against. A fixed pixel height doesn't adapt to content length,
  // so cap it relative to the viewport instead and let the content wrapper
  // (which already sets overflow: 'auto' for isNarrowLayout, see above)
  // handle scrolling past that cap.
  const panelGroupStyle: React.CSSProperties = isNarrowLayout
    ? { height: 'calc(100vh - 200px)', minHeight: 500, overflow: 'auto' }
    : { height: '100%', minHeight: 500, overflow: 'hidden' };

  // Shared divider styles for the palette/properties panes — narrow layouts
  // stack panes vertically so the divider moves from a side border to a
  // top/bottom border. Reused at every pane edge instead of repeating the
  // isNarrowLayout/isDark ternary at each call site.
  const dividerColor = isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9';
  const paletteDividerStyle: React.CSSProperties = {
    borderRight: isNarrowLayout ? 'none' : `1px solid ${dividerColor}`,
    borderBottom: isNarrowLayout ? `1px solid ${dividerColor}` : 'none',
  };
  const propertiesDividerStyle: React.CSSProperties = {
    borderLeft: isNarrowLayout ? 'none' : `1px solid ${dividerColor}`,
    borderTop: isNarrowLayout ? `1px solid ${dividerColor}` : 'none',
  };

  // Memoize metadata to avoid unnecessary syncs - single dependency instead of 18
  const metadataMemo = useMemo(
    () => initialSchema?.metadata,
    [JSON.stringify(initialSchema?.metadata)]
  );

  // Sync external metadata changes (Form Info tab) into internal schema.
  // Simplified unidirectional data flow - parent is source of truth
  React.useEffect(() => {
    if (!metadataMemo) return;
    setSchema((prev) => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        ...metadataMemo,
      },
    }));
  }, [metadataMemo]);

  // One-time sync of controls + sections when initialSchema first arrives with data
  // (handles the async gap between component mount and API data loading in parent)
  const controlsSynced = React.useRef(false);
  React.useEffect(() => {
    if (controlsSynced.current) return;
    if (initialSchema?.controls && initialSchema.controls.length > 0) {
      controlsSynced.current = true;
      setSchema((prev) => ({
        ...prev,
        controls: initialSchema.controls,
        sections: initialSchema.sections ?? prev.sections,
        apiActions: initialSchema.apiActions ?? prev.apiActions,
      }));
      if (initialSchema.apiActions) {
        setApiActions(initialSchema.apiActions);
      }
    }
  }, [initialSchema?.controls]);

  // Load API Actions from schema when it changes
  React.useEffect(() => {
    if (schema.apiActions) {
      setApiActions(schema.apiActions);
    }
  }, []);

  const selectedControl = useMemo(
    () => schema.controls.find((c) => c.id === selectedControlId) || null,
    [schema.controls, selectedControlId]
  );

  /**
   * Helper to update undo/redo state and action previews
   */
  const updateUndoRedoState = useCallback(() => {
    setUndoCount(undoRedoManager.getHistoryLength() - 1);
    setRedoCount(undoRedoManager.getRedoLength());
    setUndoActionPreview(undoRedoManager.getUndoDescription());
    setRedoActionPreview(undoRedoManager.getRedoDescription());
  }, [undoRedoManager]);

  /**
   * Show toast notification
   * Phase 4: Success/Error feedback
   */
  const showToast = useCallback((message: string, type: ToastType = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  }, []);

  /**
   * Update schema and trigger callback.
   * Phase 4: Now accepts action description for undo/redo tooltips
   */
  const updateSchema = useCallback(
    (newSchema: FormSchema, actionDescription: string = 'Change') => {
      setSchema(newSchema);
      undoRedoManager.push(newSchema, actionDescription);
      updateUndoRedoState();
      onSchemaChange?.(newSchema);
    },
    [onSchemaChange, undoRedoManager, updateUndoRedoState]
  );

  const updateApiActions = useCallback(
    (newActions: any[]) => {
      setApiActions(newActions);
      updateSchema({
        ...schema,
        apiActions: newActions,
      }, 'Update API Actions');
    },
    [schema, updateSchema]
  );

  /**
   * Add a new control — inserts into a targeted tab, after the selected control, or appends.
   * Uses functional setState to always get latest state.
   */
  const addControl = useCallback(
    (control: FormControl) => {
      setSchema((current) => {
        let newSchema: FormSchema;
        if (selectedTabId) {
          const insertIntoTab = (controls: FormControl[]): FormControl[] =>
            controls.map((c) => {
              if (c.type === 'tab' && c.config?.tabs) {
                const tabs = (c.config.tabs as any[]).map((tab: any) =>
                  tab.id === selectedTabId
                    ? { ...tab, controls: [...(tab.controls || []), control] }
                    : tab
                );
                return { ...c, config: { ...c.config, tabs } };
              }
              return c;
            });
          newSchema = { ...current, controls: insertIntoTab(current.controls) };
        } else {
          const newControls = [...current.controls];
          if (selectedControlId) {
            const idx = newControls.findIndex((c) => c.id === selectedControlId);
            if (idx !== -1) {
              newControls.splice(idx + 1, 0, control);
            } else {
              newControls.push(control);
            }
          } else {
            newControls.push(control);
          }
          newSchema = { ...current, controls: newControls };
        }
        undoRedoManager.push(newSchema, `Add ${control.type} control`);
        updateUndoRedoState();
        onSchemaChange?.(newSchema);

        // Show success feedback
        showToast(`${control.type} control added`, 'success');

        return newSchema;
      });
    },
    [selectedTabId, selectedControlId, undoRedoManager, onSchemaChange, updateUndoRedoState, showToast]
  );

  /**
   * Update selected control properties
   */
  const updateControl = useCallback(
    (id: string, updates: DesignerControlPatch) => {
      setSchema((current) => {
        const control = current.controls.find((c) => c.id === id);
        const controlLabel = control?.label || control?.type || 'control';
        const newSchema = {
          ...current,
          controls: current.controls.map((c) =>
            c.id === id ? { ...c, ...updates } : c
          ),
        };
        undoRedoManager.push(newSchema, `Update ${controlLabel}`);
        updateUndoRedoState();
        onSchemaChange?.(newSchema);
        return newSchema;
      });
    },
    [undoRedoManager, onSchemaChange, updateUndoRedoState]
  );

  /**
   * Delete control from form
   */
  const deleteControl = useCallback(
    (id: string) => {
      if (selectedControlId === id) {
        setSelectedControlId(null);
      }
      setSchema((current) => {
        const control = current.controls.find((c) => c.id === id);
        const controlLabel = control?.label || control?.type || 'control';
        const newSchema = {
          ...current,
          controls: current.controls.filter((c) => c.id !== id),
        };
        undoRedoManager.push(newSchema, `Delete ${controlLabel}`);
        updateUndoRedoState();
        onSchemaChange?.(newSchema);

        // Show success feedback
        showToast(`"${controlLabel}" deleted`, 'error');

        return newSchema;
      });
    },
    [selectedControlId, undoRedoManager, onSchemaChange, updateUndoRedoState, showToast]
  );

  /**
   * Reorder controls (for drag-drop)
   */
  const reorderControls = useCallback(
    (controlIds: string[]) => {
      setSchema((current) => {
        const controlMap = new Map(current.controls.map((c) => [c.id, c]));
        const newControls = controlIds
          .map((id) => controlMap.get(id))
          .filter((c) => c !== undefined) as FormControl[];
        const newSchema = {
          ...current,
          controls: newControls,
        };
        undoRedoManager.push(newSchema, 'Reorder controls');
        updateUndoRedoState();
        onSchemaChange?.(newSchema);
        return newSchema;
      });
    },
    [undoRedoManager, onSchemaChange, updateUndoRedoState]
  );

  /**
   * Duplicate control
   */
  /**
   * Show duplicate control dialog
   * Phase 4: Enhanced with placement options
   */
  const showDuplicateDialog = useCallback((id: string) => {
    setControlToDuplicate(id);
    setDuplicateDialogOpen(true);
  }, []);

  /**
   * Duplicate a control with placement option
   * Phase 4: Enhanced with placement control
   */
  const duplicateControl = useCallback(
    (id: string, placement: PlacementOption = 'after') => {
      setSchema((current) => {
        const control = current.controls.find((c) => c.id === id);
        if (!control) return current;

        const controlLabel = control.label || control.type || 'control';
        const newControl: FormControl = {
          ...control,
          id: `${control.id}-copy-${Date.now()}`,
          label: `${control.label} (Copy)`,
        };

        const idx = current.controls.findIndex((c) => c.id === id);
        const newControls = [...current.controls];

        // Determine insertion index based on placement
        let insertIndex: number;
        switch (placement) {
          case 'before':
            insertIndex = idx;
            break;
          case 'after':
            insertIndex = idx + 1;
            break;
          case 'start':
            insertIndex = 0;
            break;
          case 'end':
            insertIndex = newControls.length;
            break;
          default:
            insertIndex = idx + 1;
        }

        newControls.splice(insertIndex, 0, newControl);

        const newSchema = { ...current, controls: newControls };
        undoRedoManager.push(newSchema, `Duplicate ${controlLabel}`);
        updateUndoRedoState();
        onSchemaChange?.(newSchema);
        return newSchema;
      });
    },
    [undoRedoManager, onSchemaChange, updateUndoRedoState]
  );

  /**
   * Handle duplicate dialog confirm
   */
  const handleDuplicateConfirm = useCallback((placement: PlacementOption) => {
    if (controlToDuplicate) {
      const control = schema.controls.find((c) => c.id === controlToDuplicate);
      const controlLabel = control?.label || control?.type || 'Control';
      duplicateControl(controlToDuplicate, placement);
      setDuplicateDialogOpen(false);
      setControlToDuplicate(null);
      showToast(`"${controlLabel}" duplicated successfully`, 'success');
    }
  }, [controlToDuplicate, duplicateControl, schema.controls, showToast]);

  /**
   * Handle duplicate dialog cancel
   */
  const handleDuplicateCancel = useCallback(() => {
    setDuplicateDialogOpen(false);
    setControlToDuplicate(null);
  }, []);

  /**
   * Update form settings
   */
  const updateFormSettings = useCallback(
    (updates: Partial<FormSchema>) => {
      const current = schema;
      const newSchema = {
        ...current,
        ...updates,
      };
      updateSchema(newSchema);
    },
    [updateSchema]
  );

  /**
   * Update form metadata
   */
  const updateMetadata = useCallback(
    (updates: Partial<FormSchema['metadata']>) => {
      const current = schema;
      const newSchema = {
        ...current,
        metadata: {
          ...current.metadata,
          ...updates,
        },
      };
      updateSchema(newSchema);
    },
    [updateSchema]
  );

  /**
   * Undo last change
   * Phase 4: Updates action previews for tooltips
   */
  const handleUndo = useCallback(() => {
    const previousSchema = undoRedoManager.undo();
    if (previousSchema) {
      setSchema(previousSchema);
      updateUndoRedoState();
      onSchemaChange?.(previousSchema);
    }
  }, [onSchemaChange, undoRedoManager, updateUndoRedoState]);

  /**
   * Redo last undone change
   * Phase 4: Updates action previews for tooltips
   */
  const handleRedo = useCallback(() => {
    const nextSchema = undoRedoManager.redo();
    if (nextSchema) {
      setSchema(nextSchema);
      updateUndoRedoState();
      onSchemaChange?.(nextSchema);
    }
  }, [onSchemaChange, undoRedoManager, updateUndoRedoState]);

  /**
   * Save form
   */
  const handleSave = async () => {
    setIsSaving(true);
    setSaveStatus('idle');
    try {
      await onSave?.();
      setSaveStatus('success');
      showToast('Form saved successfully!', 'success');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      setSaveStatus('error');
      const errorMessage = error instanceof Error ? error.message : 'Failed to save form';
      showToast(errorMessage, 'error');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Show keyboard shortcut feedback (Priority 1.2)
   */
  const showShortcutFeedback = useCallback((message: string) => {
    setShortcutFeedback(message);
    setTimeout(() => setShortcutFeedback(null), 1500);
  }, []);

  /**
   * Keyboard navigation in designer canvas (Priority 1.3 - Accessibility)
   * Arrow keys to navigate between controls, Enter/Space to select, Ctrl+Arrow to reorder
   */
  const handleCanvasKeyDown = useCallback((e: React.KeyboardEvent) => {
    const controls = schema.controls;
    if (controls.length === 0) return;

    const currentIndex = controls.findIndex((c) => c.id === selectedControlId);

    // Arrow Up/Down: Navigate between controls (plain arrows only — Ctrl+Arrow is the
    // reorder shortcut below; without the `!e.ctrlKey` guard here, these branches matched
    // on key alone and always won since they're checked first, making the Ctrl+Arrow
    // reorder branches below permanently unreachable dead code).
    if (e.key === 'ArrowDown' && !e.ctrlKey) {
      e.preventDefault();
      const nextIndex = currentIndex < controls.length - 1 ? currentIndex + 1 : 0;
      setSelectedControlId(controls[nextIndex].id);
      showShortcutFeedback(`Selected: ${controls[nextIndex].label || controls[nextIndex].type}`);
    } else if (e.key === 'ArrowUp' && !e.ctrlKey) {
      e.preventDefault();
      const prevIndex = currentIndex > 0 ? currentIndex - 1 : controls.length - 1;
      setSelectedControlId(controls[prevIndex].id);
      showShortcutFeedback(`Selected: ${controls[prevIndex].label || controls[prevIndex].type}`);
    }
    // Enter or Space: Select control (if not already selected)
    else if ((e.key === 'Enter' || e.key === ' ') && currentIndex === -1 && controls.length > 0) {
      e.preventDefault();
      setSelectedControlId(controls[0].id);
      showShortcutFeedback(`Selected: ${controls[0].label || controls[0].type}`);
    }
    // Ctrl+ArrowUp: Move control up in order
    else if (e.ctrlKey && e.key === 'ArrowUp' && currentIndex > 0) {
      e.preventDefault();
      const ids = controls.map((c) => c.id);
      [ids[currentIndex - 1], ids[currentIndex]] = [ids[currentIndex], ids[currentIndex - 1]];
      reorderControls(ids);
      showShortcutFeedback('Moved up');
    }
    // Ctrl+ArrowDown: Move control down in order
    else if (e.ctrlKey && e.key === 'ArrowDown' && currentIndex < controls.length - 1) {
      e.preventDefault();
      const ids = controls.map((c) => c.id);
      [ids[currentIndex], ids[currentIndex + 1]] = [ids[currentIndex + 1], ids[currentIndex]];
      reorderControls(ids);
      showShortcutFeedback('Moved down');
    }
  }, [schema.controls, selectedControlId, showShortcutFeedback, reorderControls]);

  /**
   * Handle keyboard shortcuts with visual feedback
   */
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === 'z') {
          e.preventDefault();
          handleUndo();
          showShortcutFeedback('Undo (Ctrl+Z)');
        } else if (e.key === 'y') {
          e.preventDefault();
          handleRedo();
          showShortcutFeedback('Redo (Ctrl+Y)');
        } else if (e.key === 's') {
          e.preventDefault();
          handleSave();
          showShortcutFeedback('Save (Ctrl+S)');
        }
      }
      if (e.key === 'Delete' && selectedControlId) {
        e.preventDefault();
        deleteControl(selectedControlId);
        showShortcutFeedback('Delete');
      }
    },
    [selectedControlId, handleUndo, handleRedo, handleSave, deleteControl, showShortcutFeedback]
  );

  React.useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  /**
   * Warn user before leaving page with unsaved changes
   * Issue #4: Data loss prevention
   */
  React.useEffect(() => {
    const hasUnsavedChanges = undoCount > 0 && saveStatus !== 'success';

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        // Modern browsers require returnValue to be set
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [undoCount, saveStatus]);

  /**
   * Auto-save draft system (Priority 1.2)
   * Saves drafts to localStorage every 30 seconds when there are unsaved changes
   */
  const getDraftKey = useCallback(() => {
    const formID = schema.metadata?.formID || 'unknown';
    return `atlas-forms-draft-${formID}`;
  }, [schema.metadata?.formID]);

  // Restore draft on mount if it exists
  const [draftRestored, setDraftRestored] = useState(false);
  React.useEffect(() => {
    if (draftRestored) return;

    try {
      const draftKey = getDraftKey();
      const draftJson = localStorage.getItem(draftKey);

      if (draftJson) {
        const draft = JSON.parse(draftJson);
        const draftAge = Date.now() - (draft.timestamp || 0);

        // Only restore drafts less than 24 hours old
        if (draftAge < 24 * 60 * 60 * 1000) {
          // Ask user if they want to restore
          if (window.confirm('A draft version of this form was found. Would you like to restore it?')) {
            setSchema(draft.schema);
            undoRedoManager.push(draft.schema, 'Restore draft');
            updateUndoRedoState();
            console.info('[FormBuilderCanvas] Draft restored from localStorage');
          } else {
            // User declined, remove the draft
            localStorage.removeItem(draftKey);
          }
        } else {
          // Draft too old, remove it
          localStorage.removeItem(draftKey);
        }
      }
    } catch (error) {
      console.warn('[FormBuilderCanvas] Failed to restore draft:', error);
    } finally {
      setDraftRestored(true);
    }
  }, [draftRestored, getDraftKey, undoRedoManager]);

  // Auto-save draft periodically when there are unsaved changes
  React.useEffect(() => {
    if (!draftRestored) return; // Don't start auto-save until draft restoration is checked

    const hasUnsavedChanges = undoCount > 0 && saveStatus !== 'success';
    if (!hasUnsavedChanges) return;

    const saveDraft = () => {
      try {
        const draftKey = getDraftKey();
        const draft = {
          schema,
          timestamp: Date.now(),
        };
        localStorage.setItem(draftKey, JSON.stringify(draft));
        console.debug('[FormBuilderCanvas] Draft auto-saved to localStorage');
      } catch (error) {
        console.warn('[FormBuilderCanvas] Failed to save draft:', error);
      }
    };

    // Auto-save every 30 seconds
    const intervalId = setInterval(saveDraft, 30000);

    return () => clearInterval(intervalId);
  }, [schema, undoCount, saveStatus, draftRestored, getDraftKey]);

  // Clear draft when successfully saved
  React.useEffect(() => {
    if (saveStatus === 'success') {
      try {
        const draftKey = getDraftKey();
        localStorage.removeItem(draftKey);
        console.debug('[FormBuilderCanvas] Draft cleared from localStorage after successful save');
      } catch (error) {
        console.warn('[FormBuilderCanvas] Failed to clear draft:', error);
      }
    }
  }, [saveStatus, getDraftKey]);

  const panelHeader = (title: string, badge?: number) => (
    <div style={{
      padding: '10px 14px',
      borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}`,
      display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
    }}>
      <span style={{
        fontSize: 11, fontWeight: 700, textTransform: 'uppercase' as const,
        letterSpacing: '0.08em', color: isDark ? '#475569' : '#9ca3af',
      }}>{title}</span>
      {badge !== undefined && (
        <span style={{
          fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 10,
          background: isDark ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.1)',
          color: '#16a34a',
        }}>{badge}</span>
      )}
    </div>
  );

  // When forcedTab is set, use it as the active tab (overrides internal state)
  const effectiveTab = forcedTab ?? activeTab;

  // Determine default tab from prop or state
  // Map 'designer' → 'design' to match internal TabType
  React.useEffect(() => {
    if (defaultTab) {
      let mappedTab: TabType;
      if (defaultTab === 'designer') mappedTab = 'design';
      else if (defaultTab === 'json') mappedTab = 'json';
      else if (defaultTab === 'settings') mappedTab = 'settings';
      else mappedTab = 'design';

      setActiveTab(mappedTab);
    }
  }, [defaultTab]);

  // Filter tabs based on visibility flags
  const visibleTabs = React.useMemo(() => {
    let tabs: TabType[] = ['design', 'validation', 'settings', 'plugins', 'preview', 'json'];

    // If hideNavigationTabs is true, hide all tabs
    if (hideNavigationTabs) {
      return [];
    }

    // Filter individual tabs
    if (hideDesignerTab) {
      tabs = tabs.filter(t => t !== 'design');
    }
    if (hideJsonTab) {
      tabs = tabs.filter(t => t !== 'json');
    }
    if (hideSettingsTab) {
      tabs = tabs.filter(t => t !== 'settings');
    }

    return tabs;
  }, [hideDesignerTab, hideJsonTab, hideSettingsTab, hideNavigationTabs]);

  return (
    <div className="af-form-builder" style={{ '--zoom': `${zoomLevel}%` } as React.CSSProperties}>
      {/* Tabs + toolbar bar — hidden when forcedTab is set (outer component drives tabs) */}
      <div
        role="toolbar"
        aria-label="Form builder toolbar"
        style={{
          display: forcedTab ? 'none' : 'flex', alignItems: 'center', padding: '0 12px', gap: 2, flexShrink: 0, minHeight: 44,
          background: isDark ? 'rgba(2,6,23,0.95)' : '#fff',
          borderBottom: `1px solid ${isDark ? 'rgba(34,197,94,0.1)' : '#e5e7eb'}`,
        }}
      >
        {/* Tab navigation (Priority 1.3 - Accessibility) */}
        <div role="tablist" aria-label="Form builder tabs" style={{ display: 'flex', gap: 2 }}>
          {visibleTabs.map((tab) => (
            <button
              key={tab}
              role="tab"
              aria-selected={activeTab === tab}
              aria-controls={`${tab}-panel`}
              aria-label={`${tab.charAt(0).toUpperCase() + tab.slice(1)} tab`}
              onClick={() => {
                setActiveTab(tab);
                onTabChange?.(tab);
              }}
              style={{
                padding: '10px 14px', background: 'transparent', border: 'none',
                borderBottom: `2px solid ${activeTab === tab ? (isDark ? '#22c55e' : '#16a34a') : 'transparent'}`,
                color: activeTab === tab ? (isDark ? '#22c55e' : '#16a34a') : (isDark ? '#64748b' : '#6b7280'),
                cursor: 'pointer', fontSize: 13, fontWeight: activeTab === tab ? 600 : 400,
                whiteSpace: 'nowrap', transition: prefersReducedMotion ? 'none' : 'color 0.15s, border-color 0.15s',
              }}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        <div style={{ flex: 1 }} />

        {/* Grid toggle (Priority 1.3 - Accessibility) */}
        <button
          onClick={() => setShowGrid(!showGrid)}
          title="Toggle Grid"
          aria-label="Toggle grid visibility"
          aria-pressed={showGrid}
          style={{
            padding: '4px 8px', background: showGrid ? (isDark ? 'rgba(34,197,94,0.12)' : 'rgba(22,163,74,0.08)') : 'transparent',
            border: `1px solid ${showGrid ? (isDark ? '#22c55e' : '#86efac') : (isDark ? 'rgba(51,65,85,0.5)' : '#e5e7eb')}`,
            borderRadius: 4, color: showGrid ? (isDark ? '#22c55e' : '#16a34a') : (isDark ? '#64748b' : '#6b7280'),
            cursor: 'pointer', fontSize: 11, fontWeight: 600,
          }}
        >Grid</button>

        {/* Zoom controls (Priority 1.3 - Accessibility) */}
        <div role="group" aria-label="Zoom controls" style={{ display: 'flex', alignItems: 'center', marginLeft: 6, gap: 2 }}>
          <button
            onClick={() => setZoomLevel(Math.max(50, zoomLevel - 10))}
            title="Zoom Out (Ctrl+-)"
            aria-label={`Zoom out, current zoom level ${zoomLevel}%`}
            aria-disabled={zoomLevel <= 50}
            disabled={zoomLevel <= 50}
            style={{
              padding: '4px 7px', background: 'transparent',
              border: `1px solid ${isDark ? 'rgba(51,65,85,0.5)' : '#e5e7eb'}`, borderRadius: 4,
              color: isDark ? '#64748b' : '#6b7280', cursor: zoomLevel <= 50 ? 'not-allowed' : 'pointer', fontSize: 13,
              opacity: zoomLevel <= 50 ? 0.5 : 1,
            }}
          >−</button>
          <span
            role="status"
            aria-live="polite"
            aria-atomic="true"
            style={{
              minWidth: 36, textAlign: 'center', fontSize: 11, fontWeight: 600,
              color: isDark ? '#64748b' : '#6b7280',
            }}
          >{zoomLevel}%</span>
          <button
            onClick={() => setZoomLevel(Math.min(200, zoomLevel + 10))}
            title="Zoom In (Ctrl++)"
            aria-label={`Zoom in, current zoom level ${zoomLevel}%`}
            aria-disabled={zoomLevel >= 200}
            disabled={zoomLevel >= 200}
            style={{
              padding: '4px 7px', background: 'transparent',
              border: `1px solid ${isDark ? 'rgba(51,65,85,0.5)' : '#e5e7eb'}`, borderRadius: 4,
              color: isDark ? '#64748b' : '#6b7280', cursor: zoomLevel >= 200 ? 'not-allowed' : 'pointer', fontSize: 13,
              opacity: zoomLevel >= 200 ? 0.5 : 1,
            }}
          >+</button>
          {zoomLevel !== 100 && (
            <button
              onClick={() => setZoomLevel(100)}
              title="Reset Zoom"
              aria-label="Reset zoom to 100%"
              style={{
                padding: '4px 6px', background: 'transparent',
                border: `1px solid ${isDark ? 'rgba(51,65,85,0.5)' : '#e5e7eb'}`, borderRadius: 4,
                color: isDark ? '#475569' : '#9ca3af', cursor: 'pointer', fontSize: 10,
              }}
            >↺</button>
          )}
        </div>

        {/* Undo / Redo (Priority 1.3 - Accessibility) - Phase 4: Enhanced with action previews */}
        <div role="group" aria-label="Undo and redo actions" style={{ display: 'flex', gap: 2, marginLeft: 6 }}>
          <button
            onClick={handleUndo}
            disabled={undoCount === 0}
            title={undoActionPreview ? `Undo: ${undoActionPreview} (Ctrl+Z)` : "Undo (Ctrl+Z)"}
            aria-label={`Undo${undoActionPreview ? `: ${undoActionPreview}` : ' last action'}${undoCount > 0 ? `, ${undoCount} ${undoCount === 1 ? 'action' : 'actions'} available` : ', no actions to undo'}`}
            aria-disabled={undoCount === 0}
            style={{
              padding: '4px 8px', background: 'transparent',
              border: `1px solid ${isDark ? 'rgba(51,65,85,0.5)' : '#e5e7eb'}`, borderRadius: 4,
              color: isDark ? '#64748b' : '#6b7280',
              cursor: undoCount === 0 ? 'not-allowed' : 'pointer', fontSize: 13, opacity: undoCount === 0 ? 0.35 : 1,
            }}
          >↩</button>
          <button
            onClick={handleRedo}
            disabled={redoCount === 0}
            title={redoActionPreview ? `Redo: ${redoActionPreview} (Ctrl+Y)` : "Redo (Ctrl+Y)"}
            aria-label={`Redo${redoActionPreview ? `: ${redoActionPreview}` : ' last undone action'}${redoCount > 0 ? `, ${redoCount} ${redoCount === 1 ? 'action' : 'actions'} available` : ', no actions to redo'}`}
            aria-disabled={redoCount === 0}
            style={{
              padding: '4px 8px', background: 'transparent',
              border: `1px solid ${isDark ? 'rgba(51,65,85,0.5)' : '#e5e7eb'}`, borderRadius: 4,
              color: isDark ? '#64748b' : '#6b7280',
              cursor: redoCount === 0 ? 'not-allowed' : 'pointer', fontSize: 13, opacity: redoCount === 0 ? 0.35 : 1,
            }}
          >↪</button>
        </div>

        {/* Fullscreen Button (Phase 4: Fullscreen mode with live preview split view) */}
        <button
          onClick={() => setIsFullscreenOpen(true)}
          title="Fullscreen Mode - Split view with live preview (F11)"
          aria-label="Open fullscreen mode with split view"
          style={{
            padding: '4px 10px',
            background: 'transparent',
            border: `1px solid ${isDark ? 'rgba(51,65,85,0.5)' : '#e5e7eb'}`,
            borderRadius: 4,
            color: isDark ? '#64748b' : '#6b7280',
            cursor: 'pointer',
            fontSize: 13,
            marginLeft: 6,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
          </svg>
          Split View
        </button>

        {/* Save Status Indicator (Priority 1.2) */}
        <div style={{
          marginLeft: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 12,
        }}>
          {/* Status badge */}
          {(() => {
            const hasUnsavedChanges = undoCount > 0 && saveStatus !== 'success';

            if (isSaving) {
              return (
                <span style={{
                  color: isDark ? '#94a3b8' : '#64748b',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}>
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: '#f59e0b',
                    animation: prefersReducedMotion ? 'none' : 'pulse 1.5s ease-in-out infinite',
                  }} />
                  Saving…
                </span>
              );
            }

            if (saveStatus === 'success') {
              return (
                <span style={{
                  color: '#16a34a',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontWeight: 500,
                }}>
                  <CheckIcon width={14} height={14} />
                  All changes saved
                </span>
              );
            }

            if (saveStatus === 'error') {
              return (
                <span style={{
                  color: '#dc2626',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontWeight: 500,
                }}>
                  ✕ Save failed
                </span>
              );
            }

            if (hasUnsavedChanges) {
              return (
                <span style={{
                  color: isDark ? '#94a3b8' : '#64748b',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}>
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: '#f59e0b',
                  }} />
                  Unsaved changes
                </span>
              );
            }

            return null;
          })()}

          {/* Save button (Priority 1.3 - Accessibility) */}
          {onSave && !hideSaveButton && (
            <button
              onClick={handleSave}
              disabled={isSaving}
              title="Save (Ctrl+S)"
              aria-label={isSaving ? 'Saving form, please wait' : saveStatus === 'error' ? 'Save form, previous save failed' : saveStatus === 'success' ? 'Save form, all changes saved' : 'Save form'}
              aria-disabled={isSaving}
              style={{
                padding: '5px 14px', border: 'none', borderRadius: 6,
                background: saveStatus === 'error' ? '#dc2626' : '#16a34a',
                color: '#fff',
                fontSize: 12, fontWeight: 700, cursor: isSaving ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', gap: 6,
                opacity: isSaving ? 0.8 : 1,
              }}
            >
              {isSaving && <LoadingSpinner size={14} color="#ffffff" />}
              {isSaving ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      </div>

      {/* Content (Priority 1.3 - Accessibility) */}
      <div
        role="tabpanel"
        id={`${effectiveTab}-panel`}
        aria-labelledby={`${effectiveTab}-tab`}
        className="af-form-builder__content"
        style={
          effectiveTab === 'design'
            ? { flex: 1, minHeight: 0, overflow: isNarrowLayout ? 'auto' : 'hidden', padding: 0 }
            : { flex: 1, minHeight: 0, overflow: 'auto', padding: '1rem' }
        }
      >
        <TabErrorBoundary key={effectiveTab}>
        {effectiveTab === 'design' && (
          <PanelGroup direction={isNarrowLayout ? 'vertical' : 'horizontal'} style={panelGroupStyle}>

            {/* Left — Field Palette (conditionally hidden) */}
            {!hideFieldToolbox && (
              <>
                <Panel {...paletteSize}>
                  <div style={{
                    height: '100%',
                    ...paletteDividerStyle,
                    background: isDark ? 'rgba(0,0,0,0.15)' : '#fafafa',
                    display: 'flex', flexDirection: 'column', overflow: 'hidden',
                  }}>
                    {panelHeader('Fields')}
                    <div style={{ flex: 1, overflow: 'auto' }}>
                      <ControlsTab onAddControl={addControl} schema={schema} />
                    </div>
                  </div>
                </Panel>

                <PanelResizeHandle className="af-panel-resize-handle" />
              </>
            )}

            {/* Center — Canvas */}
            <Panel {...canvasSize}>
              <div style={{
                height: '100%', display: 'flex', flexDirection: 'column',
                minWidth: 0, overflow: 'hidden',
              }}>
                {panelHeader('Canvas', schema.controls.length)}
                {selectedTabId && (
                  <div style={{
                    padding: '6px 12px', flexShrink: 0,
                    background: isDark ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.1)',
                    borderBottom: `1px solid ${isDark ? 'rgba(34,197,94,0.3)' : 'rgba(34,197,94,0.2)'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    fontSize: 12, fontWeight: 500,
                    color: isDark ? '#22c55e' : '#16a34a',
                  }}>
                    <span>Adding to tab: <strong>{selectedTabId}</strong></span>
                    <button
                      type="button"
                      onClick={() => setSelectedTabId(null)}
                      style={{
                        border: 'none', background: 'transparent', cursor: 'pointer',
                        fontSize: 12, padding: '2px 6px',
                        color: isDark ? '#22c55e' : '#16a34a',
                      }}
                    >× Clear</button>
                  </div>
                )}
                <div style={{ flex: 1, overflow: 'auto' }}>
                  <DesignTab
                    schema={schema}
                    selectedControlId={selectedControlId}
                    onSelectControl={setSelectedControlId}
                    onAddControl={addControl}
                    onDeleteControl={deleteControl}
                    onDuplicateControl={showDuplicateDialog}
                    onReorderControls={reorderControls}
                    zoomLevel={zoomLevel}
                    showGrid={showGrid}
                    selectedTabId={selectedTabId}
                    onSelectTab={setSelectedTabId}
                    compact
                  />
                </div>
              </div>
            </Panel>

            {/* Right — Properties (conditionally hidden) */}
            {!hidePropertiesPanel && (
              <>
                <PanelResizeHandle className="af-panel-resize-handle" />

                <Panel {...propertiesSize}>
                  <div style={{
                    height: '100%',
                    ...propertiesDividerStyle,
                    background: isDark ? 'rgba(0,0,0,0.15)' : '#fafafa',
                    display: 'flex', flexDirection: 'column', overflow: 'hidden',
                  }}>
                    {panelHeader('Properties')}
                    <div style={{ flex: 1, overflow: 'auto' }}>
                      <PropertiesTab control={selectedControl} onUpdateControl={updateControl} />
                    </div>
                    {selectedControl && (
                      <div style={{
                        padding: 10, flexShrink: 0,
                        borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}`,
                        display: 'flex', gap: 8,
                      }}>
                        <button
                          type="button"
                          onClick={() => showDuplicateDialog(selectedControl.id)}
                          style={{
                            flex: 1, padding: '7px 10px', border: 'none', borderRadius: 6,
                            background: isDark ? 'rgba(51,65,85,0.5)' : '#e2e8f0',
                            color: isDark ? '#94a3b8' : '#475569',
                            fontSize: 12, fontWeight: 600, cursor: 'pointer',
                          }}
                        >Duplicate</button>
                        <button
                          type="button"
                          onClick={() => { deleteControl(selectedControl.id); setSelectedControlId(null); }}
                          style={{
                            flex: 1, padding: '7px 10px', border: 'none', borderRadius: 6,
                            background: '#ef4444', color: '#fff',
                            fontSize: 12, fontWeight: 600, cursor: 'pointer',
                          }}
                        >Delete</button>
                      </div>
                    )}
                  </div>
                </Panel>
              </>
            )}

          </PanelGroup>
        )}

        {effectiveTab === 'wysiwyg' && (
          <PanelGroup direction={isNarrowLayout ? 'vertical' : 'horizontal'} style={panelGroupStyle}>

            {/* Left — Field Palette (conditionally hidden) */}
            {!hideFieldToolbox && (
              <>
                <Panel {...paletteSize}>
                  <div style={{
                    height: '100%',
                    ...paletteDividerStyle,
                    background: isDark ? 'rgba(0,0,0,0.15)' : '#fafafa',
                    display: 'flex', flexDirection: 'column', overflow: 'hidden',
                  }}>
                    {panelHeader('Fields')}
                    <div style={{ flex: 1, overflow: 'auto' }}>
                      <ControlsTab onAddControl={addControl} schema={schema} />
                    </div>
                  </div>
                </Panel>

                <PanelResizeHandle className="af-panel-resize-handle" />
              </>
            )}

            {/* Center — WYSIWYG canvas (Priority 1.3 - Accessibility) */}
            <Panel {...canvasSize}>
              <div style={{ height: '100%', display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
              {panelHeader('Designer', schema.controls.length)}
              <div
                role="region"
                aria-label="Form designer canvas - use arrow keys to navigate controls, Ctrl+Arrow to reorder"
                tabIndex={0}
                onKeyDown={handleCanvasKeyDown}
                style={{ flex: 1, overflow: 'auto', padding: 16, outline: 'none' }}
              >
                {schema.controls.length === 0 ? (
                  <div style={{
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    height: '100%', minHeight: 320, gap: 12,
                    color: isDark ? '#475569' : '#9ca3af',
                  }}>
                    <div style={{ fontSize: 40, opacity: 0.4 }}>☰</div>
                    <div style={{ fontSize: 13, fontWeight: 500, textAlign: 'center' }}>No fields yet</div>
                    <div style={{ fontSize: 11, textAlign: 'center' }}>← Click a field type to add it</div>
                  </div>
                ) : (
                  <FormThemeProvider theme={theme as 'dark' | 'light'}>
                    {schema.controls.map((control) => {
                      const isSelected = selectedControlId === control.id;
                      const isDragging = wysiwygDraggedId === control.id;
                      const isDragOver = wysiwygDragOverId === control.id;

                      const handleDragStart = (e: React.DragEvent) => {
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/plain', control.id);
                        setWysiwygDraggedId(control.id);
                        setSelectedControlId(control.id);
                      };

                      const handleDragOver = (e: React.DragEvent) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                        if (wysiwygDraggedId && wysiwygDraggedId !== control.id) {
                          setWysiwygDragOverId(control.id);
                        }
                      };

                      const handleDrop = (e: React.DragEvent) => {
                        e.preventDefault();
                        const fromId = e.dataTransfer.getData('text/plain');
                        if (fromId && fromId !== control.id) {
                          const ids = schema.controls.map((c) => c.id);
                          const fromIdx = ids.indexOf(fromId);
                          const toIdx = ids.indexOf(control.id);
                          if (fromIdx !== -1 && toIdx !== -1) {
                            ids.splice(fromIdx, 1);
                            ids.splice(toIdx, 0, fromId);
                            reorderControls(ids);
                          }
                        }
                        setWysiwygDraggedId(null);
                        setWysiwygDragOverId(null);
                      };

                      // Kept as four longhand border-*-side properties rather than the `border`
                      // shorthand + a `borderTop` override — mixing shorthand and non-shorthand
                      // for the same value on one style object is exactly the pattern React warns
                      // about ("Updating border borderTop ... can lead to styling bugs"), which
                      // fired on every drag-over toggle here since isDragOver flips `borderTop`
                      // between a value and `undefined` while `border` is reapplied alongside it.
                      const controlBorderColor = isDragOver
                        ? (isDark ? '#f59e0b' : '#f59e0b')
                        : isSelected
                          ? (isDark ? '#22c55e' : '#16a34a')
                          : (isDark ? 'rgba(34,197,94,0.4)' : 'rgba(22,163,74,0.3)');
                      const controlBorderSide = `2px solid ${controlBorderColor}`;

                      return (
                        <div
                          key={control.id}
                          role="button"
                          aria-label={`${control.label || control.type} control${isSelected ? ', selected' : ''}`}
                          aria-pressed={isSelected}
                          tabIndex={0}
                          style={{
                            position: 'relative',
                            marginBottom: 12,
                            borderRadius: 8,
                            borderTop: isDragOver ? `3px solid #f59e0b` : controlBorderSide,
                            borderRight: controlBorderSide,
                            borderBottom: controlBorderSide,
                            borderLeft: controlBorderSide,
                            padding: 10,
                            background: isSelected
                              ? (isDark ? 'rgba(34,197,94,0.06)' : 'rgba(22,163,74,0.04)')
                              : 'transparent',
                            opacity: isDragging ? 0.35 : 1,
                            transition: prefersReducedMotion ? 'none' : 'border-color 0.12s, opacity 0.12s',
                            outline: 'none',
                            boxShadow: isSelected ? `0 0 0 3px ${isDark ? 'rgba(34,197,94,0.2)' : 'rgba(22,163,74,0.2)'}` : 'none',
                          }}
                          onFocus={() => setSelectedControlId(control.id)}
                        >
                          {isSelected && (
                            <div style={{
                              position: 'absolute', top: -9, left: 12, zIndex: 2,
                              background: isDark ? '#22c55e' : '#16a34a',
                              color: '#fff', fontSize: 10, fontWeight: 700,
                              padding: '2px 8px', borderRadius: 4,
                              textTransform: 'uppercase', letterSpacing: '0.05em',
                            }}>
                              ✔ Selected
                            </div>
                          )}
                          {control.referredFormID != null && (
                            <div style={{
                              position: 'absolute', top: -9, right: 12, zIndex: 2,
                              background: control.controlInheritType === 'import'
                                ? (isDark ? '#7c3aed' : '#6d28d9')
                                : (isDark ? '#0e7490' : '#0369a1'),
                              color: '#fff', fontSize: 10, fontWeight: 700,
                              padding: '2px 8px', borderRadius: 4,
                              letterSpacing: '0.04em', display: 'flex', alignItems: 'center', gap: 4,
                            }}>
                              {control.controlInheritType === 'import' ? '⬇ import' : '⟳ override'}
                              {' from #'}{control.referredFormID}
                            </div>
                          )}
                          <FormField
                            control={control}
                            value={undefined}
                            mode="edit"
                            onChange={() => {}}
                          />
                          {/* overlay — captures click (select) and drag (reorder) */}
                          <div
                            draggable
                            style={{
                              position: 'absolute', inset: 0, zIndex: 1,
                              cursor: isDragging ? 'grabbing' : 'grab',
                            }}
                            onClick={(e) => { e.stopPropagation(); setSelectedControlId(control.id); }}
                            onDragStart={handleDragStart}
                            onDragOver={handleDragOver}
                            onDragLeave={() => setWysiwygDragOverId(null)}
                            onDrop={handleDrop}
                            onDragEnd={() => { setWysiwygDraggedId(null); setWysiwygDragOverId(null); }}
                          />
                        </div>
                      );
                    })}
                  </FormThemeProvider>
                )}
              </div>
              </div>
            </Panel>

            {/* Right — Properties (conditionally hidden) */}
            {!hidePropertiesPanel && (
              <>
                <PanelResizeHandle className="af-panel-resize-handle" />

                <Panel {...propertiesSize}>
                  <div style={{
                    height: '100%',
                    ...propertiesDividerStyle,
                    background: isDark ? 'rgba(0,0,0,0.15)' : '#fafafa',
                    display: 'flex', flexDirection: 'column', overflow: 'hidden',
                  }}>
                    {panelHeader('Properties')}
                    <div style={{ flex: 1, overflow: 'auto' }}>
                      <PropertiesTab control={selectedControl} onUpdateControl={updateControl} />
                    </div>
                    {selectedControl && (
                      <div style={{ padding: 10, flexShrink: 0, borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}`, display: 'flex', gap: 8 }}>
                        <button
                          type="button"
                          onClick={() => showDuplicateDialog(selectedControl.id)}
                          style={{ flex: 1, padding: '7px 10px', border: 'none', borderRadius: 6, background: isDark ? 'rgba(51,65,85,0.5)' : '#e2e8f0', color: isDark ? '#94a3b8' : '#475569', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                        >Duplicate</button>
                        <button
                          type="button"
                          onClick={() => { deleteControl(selectedControl.id); setSelectedControlId(null); }}
                          style={{ flex: 1, padding: '7px 10px', border: 'none', borderRadius: 6, background: '#ef4444', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                        >Delete</button>
                      </div>
                    )}
                  </div>
                </Panel>
              </>
            )}

          </PanelGroup>
        )}

        {effectiveTab === 'validation' && (
          <ValidationTab
            controls={schema.controls}
            onUpdateControl={updateControl}
          />
        )}

        {effectiveTab === 'settings' && (
          <SettingsTab
            metadata={schema.metadata}
            sections={schema.sections}
            onUpdateMetadata={updateMetadata}
            onUpdateFormSettings={updateFormSettings}
          />
        )}

        {effectiveTab === 'preview' && (
          <PreviewTab
            schema={schema}
          />
        )}

        {effectiveTab === 'json' && (
          <JsonTab
            schema={schema}
            onSchemaChange={updateSchema}
          />
        )}

        {effectiveTab === 'plugins' && (
          <div style={{ flex: 1, overflow: 'auto' }}>
            <PluginsTab
              schema={schema}
              onAddPlugin={(control) => addControl(control)}
              onRemovePlugin={(id) => deleteControl(id)}
              onUpdatePluginConfig={(id, config) => updateControl(id, { config })}
              apiActions={apiActions}
              onUpdateApiActions={updateApiActions}
            />
          </div>
        )}
        </TabErrorBoundary>
      </div>

      {/* Keyboard Shortcut Feedback Toast (Priority 1.2) */}
      {shortcutFeedback && (
        <div style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          padding: '12px 20px',
          background: isDark ? 'rgba(15,23,42,0.95)' : 'rgba(0,0,0,0.85)',
          color: '#fff',
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 600,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          animation: prefersReducedMotion ? 'none' : 'slideInUp 0.2s ease-out',
        }}>
          <span style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: '#22c55e',
          }} />
          {shortcutFeedback}
        </div>
      )}

      {/* Duplicate Control Dialog */}
      <DuplicateControlDialog
        isOpen={duplicateDialogOpen}
        controlLabel={
          controlToDuplicate
            ? schema.controls.find((c) => c.id === controlToDuplicate)?.label ||
              schema.controls.find((c) => c.id === controlToDuplicate)?.type ||
              'Control'
            : 'Control'
        }
        onConfirm={handleDuplicateConfirm}
        onCancel={handleDuplicateCancel}
      />

      {/* Toast Notifications */}
      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={toastVisible}
        onDismiss={() => setToastVisible(false)}
      />

      {/* Fullscreen Canvas Modal (Phase 4: Fullscreen mode with live preview split view) */}
      <FullscreenCanvasModal
        isOpen={isFullscreenOpen}
        onClose={() => setIsFullscreenOpen(false)}
        schema={schema}
        selectedControlId={selectedControlId}
        onSelectControl={setSelectedControlId}
        onAddControl={addControl as any}
        onDeleteControl={deleteControl}
        onDuplicateControl={showDuplicateDialog}
        onReorderControls={reorderControls as any}
        selectedTabId={selectedTabId}
        onSelectTab={setSelectedTabId}
        zoomLevel={zoomLevel}
        showGrid={showGrid}
      />
    </div>
  );
};

FormBuilderCanvas.displayName = 'FormBuilderCanvas';
